import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

export type BindOnceBeatOptions = {
  trigger: HTMLElement;
  timeline: {
    play: () => unknown;
  };
  start?: string;
};

/**
 * Play a paused timeline once when the trigger crosses `start`.
 * Does not reverse or replay.
 */
export function bindOnceBeat({
  trigger,
  timeline,
  start = 'top 10%',
}: BindOnceBeatOptions): () => void {
  const st = ScrollTrigger.create({
    trigger,
    start,
    once: true,
    onEnter: () => {
      timeline.play();
    },
  });

  if (st.isActive || st.progress > 0) timeline.play();

  return () => st.kill();
}

export const TEXT_REVEAL_FULL = {
  y: 24,
  duration: 0.7,
} as const;

export const TEXT_REVEAL_SUBTLE = {
  y: 12,
  duration: 0.5,
} as const;

export type OnceTextRevealOptions = {
  y?: number;
  duration?: number;
  delay?: number;
  stagger?: number;
  trigger?: HTMLElement;
  start?: string;
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * One-time fade-up as the element enters view while scrolling down.
 * Transform + opacity only (no CSS filter). Does not reverse or replay.
 * No-ops when the user prefers reduced motion.
 */
export function bindOnceTextReveal(
  el: HTMLElement | HTMLElement[],
  opts?: OnceTextRevealOptions,
): () => void {
  const targets = (Array.isArray(el) ? el : [el]).filter(
    (node): node is HTMLElement => Boolean(node),
  );
  if (!targets.length) return () => undefined;

  if (
    typeof window !== 'undefined' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  ) {
    return () => undefined;
  }

  const {
    y = TEXT_REVEAL_FULL.y,
    duration = TEXT_REVEAL_FULL.duration,
    delay = 0,
    stagger = 0,
    trigger = targets[0],
    start = 'top 85%',
  } = opts ?? {};

  gsap.set(targets, { opacity: 0, y });

  const play = () => {
    gsap.to(targets, {
      opacity: 1,
      y: 0,
      duration,
      delay,
      stagger,
      ease: 'power2.out',
    });
  };

  const st = ScrollTrigger.create({
    trigger,
    start,
    once: true,
    onEnter: play,
  });

  if (st.isActive || st.progress > 0) play();

  return () => st.kill();
}
